import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Warehouse, Plus, Pencil, Trash2, Search, MapPin } from "lucide-react";
import { DomiciliosClienteDialog } from "@/components/DestinoClienteSelect";
import { esCp, esEmail, esNif } from "@/lib/validacionEs";
import { LEN } from "@/lib/limites";

const I = String.fromCharCode(0xed);
const O = String.fromCharCode(0xf3);
const A = String.fromCharCode(0xe1);
const U = String.fromCharCode(0xfa);
const N = String.fromCharCode(0xf1);
const DASH = "\u2014";

const EMPTY = {
  razon_social: "",
  nif: "",
  domicilio: "",
  municipio: "",
  cp: "",
  provincia: "",
  email: "",
};

export default function CargadoresMantPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [domCar, setDomCar] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/api/v1/partes", { params: { tipo: "cargador" } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const filtrados = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((t) =>
      [t.razon_social, t.nif, t.domicilio, t.municipio, t.provincia, t.cp, t.email].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);

  const openNew = () => {
    setEdit(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEdit(t);
    setForm({
      razon_social: t.razon_social || "",
      nif: t.nif || "",
      domicilio: t.domicilio || "",
      municipio: t.municipio || "",
      cp: t.cp || "",
      provincia: t.provincia || "",
      email: t.email || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.razon_social.trim()) {
      toast.error("Indica la raz" + O + "n social");
      return;
    }
    if (!form.nif.trim()) {
      toast.error("Indica el NIF");
      return;
    }
    if (!esNif(form.nif)) {
      toast.error("NIF / CIF / NIE no v" + A + "lido (letra de control)");
      return;
    }
    if (form.cp.trim() && !esCp(form.cp)) {
      toast.error("C" + O + "digo postal no v" + A + "lido (5 d" + I + "gitos)");
      return;
    }
    if (form.email.trim() && !esEmail(form.email)) {
      toast.error("Correo no v" + A + "lido");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        razon_social: form.razon_social.trim(),
        nif: form.nif.trim(),
        domicilio: form.domicilio.trim(),
        municipio: form.municipio.trim(),
        cp: form.cp.trim(),
        provincia: form.provincia.trim(),
        email: form.email.trim() || null,
      };
      if (edit) await api.patch(`/api/v1/partes/${edit.id}`, payload);
      else await api.post("/api/v1/partes", { tipo: "cargador", ...payload });
      setOpen(false);
      await load();
      toast.success(edit ? "Cargador actualizado" : "Cargador creado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    const ok = window.confirm("Eliminar el cargador " + (t.razon_social || t.nif) + "?");
    if (!ok) return;
    try {
      await api.delete(`/api/v1/partes/${t.id}`);
      await load();
      toast.success("Cargador eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const domicilioTxt = (t) => {
    const dom = t.domicilio || "";
    if (dom && String(t.cp || "") && dom.toLowerCase().includes(String(t.cp).toLowerCase())) return dom;
    return [t.domicilio, t.cp, t.municipio, t.provincia].filter(Boolean).join(", ") || DASH;
  };

  return (
    <div>
      <PageHeader
        title="Cargadores"
        subtitle={"Cat" + A + "logo del cargador contractual (origen). Puedes a" + N + "adir almacenes extra y elegirlos como origen en el DeCA."}
        testid="page-cargadores-mant"
        actions={
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="btn-cargador-nuevo">
            <Plus className="h-4 w-4 mr-2"/> Nuevo cargador
          </Button>
        }
      />

      <Card className="p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400"/>
          <Input
            className="pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={"Buscar raz" + O + "n social, NIF o poblaci" + O + "n"}
            aria-label="Buscar cargadores"
            data-testid="cargadores-buscar"
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="deca-table w-full min-w-[720px]">
            <thead>
              <tr>
                <th>{"Raz" + O + "n social"}</th>
                <th>NIF</th>
                <th>Domicilio</th>
                <th>Correo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((t) => (
                <tr key={t.id} data-testid={`cargador-row-${t.id}`}>
                  <td className="font-medium">{t.razon_social || DASH}</td>
                  <td className="font-mono text-xs">{t.nif || DASH}</td>
                  <td className="text-sm text-slate-600 max-w-[280px] truncate">{domicilioTxt(t)}</td>
                  <td className="text-sm">{t.email || DASH}</td>
                  <td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="mr-2" onClick={() => setDomCar(t)} aria-label="Almacenes del cargador" data-testid={`btn-dom-car-${t.id}`} disabled={!t.nif}>
                      <MapPin className="h-3.5 w-3.5" aria-hidden="true"/>
                    </Button>
                    <Button size="sm" variant="outline" className="mr-2" onClick={() => openEdit(t)} aria-label="Editar cargador" data-testid={`btn-edit-car-${t.id}`}>
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true"/>
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-700" onClick={() => remove(t)} aria-label="Eliminar cargador" data-testid={`btn-del-car-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true"/>
                    </Button>
                  </td>
                </tr>
              ))}
              {filtrados.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-500">
                    <Warehouse className="h-8 w-8 mx-auto text-slate-400 mb-2"/>
                    {items.length === 0 ? "No hay cargadores. Crea el primero." : "No hay cargadores con esa b" + U + "squeda."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar cargador" : "Nuevo cargador"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field className="sm:col-span-2" label={"Raz" + O + "n social *"}>
              <Input className="mt-1" maxLength={LEN.razonSocial} value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} data-testid="car-razon"/>
            </Field>
            <Field label="NIF *">
              <Input className="mt-1 font-mono uppercase" maxLength={LEN.nif} value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} data-testid="car-nif"/>
            </Field>
            <Field label={"C" + O + "digo postal"}>
              <Input className="mt-1" inputMode="numeric" maxLength={LEN.cp} value={form.cp} onChange={(e) => setForm({ ...form, cp: e.target.value })} data-testid="car-cp"/>
            </Field>
            <Field className="sm:col-span-2" label="Domicilio (origen)">
              <Input className="mt-1" maxLength={LEN.domicilio} value={form.domicilio} onChange={(e) => setForm({ ...form, domicilio: e.target.value })} data-testid="car-domicilio"/>
            </Field>
            <Field label="Municipio">
              <Input className="mt-1" maxLength={LEN.municipio} value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} data-testid="car-municipio"/>
            </Field>
            <Field label="Provincia">
              <Input className="mt-1" maxLength={LEN.provincia} value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} data-testid="car-provincia"/>
            </Field>
            <Field className="sm:col-span-2" label={"Correo de env" + I + "o"}>
              <Input className="mt-1" type="email" maxLength={LEN.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="logistica@empresa.com" data-testid="car-email"/>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={busy} data-testid="car-guardar">
              {busy ? "Guardando" + String.fromCharCode(0x2026) : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DomiciliosClienteDialog
        open={!!domCar}
        onOpenChange={(v) => { if (!v) setDomCar(null); }}
        cliente={domCar}
        tipo="cargador"
      />
    </div>
  );
}
